import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Download, ExternalLink, Trash2, Loader2 } from 'lucide-react';
import { MusikkFil } from '@/services/api';
import { getFilTypeIcon, getFilTypeLabel, getFilTypeColor, formatFileSize } from '../utils/fileUtils';

interface ExistingFilesListProps {
  files: MusikkFil[];
  uploadingId: number | null;
  onDownload: (fil: MusikkFil) => void;
  onDelete: (fil: MusikkFil) => void;
}

export const ExistingFilesList: React.FC<ExistingFilesListProps> = ({
  files,
  uploadingId,
  onDownload,
  onDelete,
}) => {
  if (files.length === 0) return null;

  return (
    <Card className="overflow-hidden shadow-sm">
      <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-400" />
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base sm:text-lg font-semibold">
            Eksisterende filer
          </h3>
          <Badge variant="secondary" className="text-xs">
            {files.length} {files.length === 1 ? 'fil' : 'filer'}
          </Badge>
        </div>

        <div className="space-y-2 sm:space-y-3">
          {files.map((fil) => (
            <div
              key={fil.id}
              className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-slate-50 rounded-lg border"
            >
              <div className="shrink-0">
                {getFilTypeIcon(fil.filType)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                  <span className="text-sm sm:text-base font-medium truncate">
                    {fil.navn}
                  </span>
                  <Badge className={`${getFilTypeColor(fil.filType)} text-xs shrink-0`}>
                    {getFilTypeLabel(fil.filType)}
                  </Badge>
                </div>
                {fil.filstorrelse && (
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {formatFileSize(fil.filstorrelse)} • {fil.mimeType}
                  </p>
                )}
                {fil.url && (
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">
                    {fil.url}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(fil.createdAt).toLocaleDateString('no-NO')}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDownload(fil)}
                  disabled={uploadingId === fil.id}
                  title="Last ned / Åpne"
                >
                  {fil.filType === 'upload' ? (
                    <Download className="h-4 w-4" />
                  ) : (
                    <ExternalLink className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(fil)}
                  disabled={uploadingId === fil.id}
                >
                  {uploadingId === fil.id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-destructive" />
                  ) : (
                    <Trash2 className="h-4 w-4 text-destructive" />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

